import React, { useEffect, useState } from 'react';
import { useLoadingScreen } from '../../../../hooks/useLoadingScreen';
import style from './RoleManagementAdd.module.scss';
import {
  ButtonPrimary,
  Input,
  SelectInputSearch,
} from '@skf/bt-wind-rail-commonlibrary';
import { useTranslation } from 'react-i18next';
import RoleManagementAddDataTable from './dataTable/RoleManagementAddDataTable';
import {
  RoleManagementRoute,
  UserManagementRoute,
} from '../../../../routes/routes';
import { usePathname } from '../../../../hooks/usePathname';
import { IRolesState } from '../../../../store/roles/store';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../../store/store';
import { fetchRolesInfo, postRole } from '../../../../store/roles/actions';
import { Role } from '../../../../store/roles/types';
import {
  getIndustryOptions,
  getPortalOptions,
  getPriorityOptions,
  getUserWebRolesOptions,
} from '../RoleManagement.mapper';
import { useToast } from '../../../../hooks/useToast';
import { IUserSettingsState } from '../../../../store/userSettings/store';
import { isUserAuthorized } from '../../../../services/authentication/authenticationService';
import { UserWebFeatures } from '../../../../store/userSettings/types';
interface IUserManagementAddProps {}

const RoleManagementAdd: React.FunctionComponent<IUserManagementAddProps> = () => {
  const { setLoading } = useLoadingScreen();
  const { t } = useTranslation();
  const { setPathname } = usePathname();
  const { addToast } = useToast();
  const dispatch = useDispatch();

  const userSettings: IUserSettingsState = useSelector<
    StoreState,
    IUserSettingsState
  >((state) => state.userSettings);

  useEffect(() => {
    const authorized = isUserAuthorized(userSettings, [
      UserWebFeatures.ROLE_MANAGEMENT_ACCESS,
    ]);
    if (authorized) setPathname(UserManagementRoute.path);
  }, [userSettings, userSettings.isFetching]);

  const [currentRole, setCurrentRole] = useState<Role>();
  const [selectedUserWebRole, setSelectedUserWebRole] = useState<string>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { rolesInfo, status }: IRolesState = useSelector<
    StoreState,
    IRolesState
  >((state) => state.roles);

  const getStatusMessage = (_status: number): string => {
    switch (_status) {
      case 200:
      case 201:
        return t('Role_saved_successfully');
      case 409:
        return t('Role_name_duplicated');
      default:
        return `Error ${_status}`;
    }
  };

  useEffect(() => {
    if (!isSubmitted || !status) return;

    const isSaved = status >= 200 && status < 300;

    addToast({
      message: getStatusMessage(status),
      status: isSaved ? 'ok' : 'danger',
    });

    setIsSubmitted(false);
    setLoading(false);

    if (isSaved) setPathname(RoleManagementRoute.path);
  }, [status]);

  useEffect(() => {
    if (rolesInfo) return setLoading(false);
    dispatch(fetchRolesInfo());
  }, [rolesInfo]);

  useEffect(() => {
    setCurrentRole({
      userWebRoles: [],
      name: '',
      industryName: undefined,
      btPortalIndustry: undefined,
      btPortalIndustryPriorization: undefined,
      priority: undefined,
    });
  }, []);

  const onChangeName = (name: string) => {
    if (!currentRole) return;
    setCurrentRole({
      ...currentRole,
      name: name.trim(),
    });
  };

  const onChangeIndustryName = (option: any) => {
    if (!currentRole) return;
    setCurrentRole({
      ...currentRole,
      industryName: option?.label,
      btPortalIndustry: undefined,
      btPortalIndustryPriorization: undefined,
      priority: undefined,
      userWebRoles: [],
    });
    setSelectedUserWebRole(undefined);
  };

  const onChangePortalName = (option: any) => {
    if (!currentRole) return;
    const roleInfo = rolesInfo?.find(
      (x) => x.industryName === currentRole?.industryName
    );
    const userWebRole = roleInfo?.userWebRoles.find(
      (x) => x.btPortalIndustry === option?.label
    );
    setCurrentRole({
      ...currentRole,
      btPortalIndustry: option?.label,
      priority: undefined,
      btPortalIndustryPriorization: userWebRole?.btPortalIndustryId,
      userWebRoles: [],
    });
  };

  const onChangePriority = (option: any) => {
    if (!currentRole) return;
    setCurrentRole({
      ...currentRole,
      priority: option?.value,
    });
  };

  const onChangeUserWebRole = (option: any) => {
    setSelectedUserWebRole(option?.value);
  };

  const onClickAddUserWebRole = () => {
    if (!currentRole || !selectedUserWebRole) return;
    setCurrentRole({
      ...currentRole,
      userWebRoles: [...currentRole.userWebRoles, selectedUserWebRole],
    });
    setSelectedUserWebRole(undefined);
  };

  const onRemoveUserWebRole = (userWebRole: string) => {
    if (!currentRole) return;
    setCurrentRole({
      ...currentRole,
      userWebRoles: currentRole.userWebRoles.filter(
        (_userWebRole) => userWebRole !== _userWebRole
      ),
    });
  };

  const onClickCancel = () => {
    setPathname(RoleManagementRoute.path);
  };

  const onClickSave = () => {
    setIsSubmitted(true);
    setLoading(true);
    setTimeout(() => {
      dispatch(postRole(currentRole));
    }, 23);
  };

  const canSave =
    currentRole &&
    currentRole.name.length > 0 &&
    currentRole.industryName &&
    currentRole.btPortalIndustry &&
    currentRole.priority !== undefined &&
    currentRole.userWebRoles.length > 0;
  return (
    <div className={style.form}>
      <h3>{t('Role_Info')}</h3>
      <div className={style.firstSection}>
        <Input
          placeholder={t('Role_Name')}
          value={currentRole?.name}
          onChange={onChangeName}
        />
        <SelectInputSearch
          placeHolder={t('Industry_Name')}
          options={getIndustryOptions(rolesInfo)}
          translate={t}
          onSelectedItemChanged={onChangeIndustryName}
        />
        <SelectInputSearch
          placeHolder={t('Portal_Name')}
          options={getPortalOptions(rolesInfo, currentRole?.industryName)}
          value={currentRole?.btPortalIndustry}
          translate={t}
          isDisabled={currentRole?.industryName === undefined}
          onSelectedItemChanged={onChangePortalName}
        />
        <SelectInputSearch
          placeHolder={t('Priority')}
          options={getPriorityOptions(rolesInfo, currentRole?.industryName)}
          value={currentRole?.priority}
          translate={t}
          isDisabled={currentRole?.btPortalIndustry === undefined}
          onSelectedItemChanged={onChangePriority}
        />
      </div>
      <h3>{t('User_Web_Roles')}</h3>
      <div className={style.secondSection}>
        <div className={style.header}>
          <SelectInputSearch
            placeHolder={t('User_Web_Role')}
            options={getUserWebRolesOptions(
              rolesInfo,
              currentRole?.industryName,
              currentRole?.btPortalIndustry,
              currentRole?.userWebRoles
            )}
            value={selectedUserWebRole}
            translate={t}
            isDisabled={currentRole?.btPortalIndustry === undefined}
            onSelectedItemChanged={onChangeUserWebRole}
          />
          <ButtonPrimary
            disabled={selectedUserWebRole === undefined}
            onClick={onClickAddUserWebRole}
          >
            {t('Add_User_Web_Role')}
          </ButtonPrimary>
        </div>
        <div>
          <RoleManagementAddDataTable
            userWebRoles={currentRole?.userWebRoles || []}
            onRemove={onRemoveUserWebRole}
          />
        </div>
      </div>
      <div>
        <div className={style.navigation}>
          <ButtonPrimary onClick={onClickCancel} outline>
            {t('Cancel')}
          </ButtonPrimary>
          <ButtonPrimary onClick={onClickSave} disabled={!canSave}>
            {t('Save')}
          </ButtonPrimary>
        </div>
      </div>
    </div>
  );
};

export default RoleManagementAdd;
